/* eslint-disable react/no-unstable-nested-components --
 * MasterDataTable の columns に並ぶ cell 関数は React component ではなく
 * テーブルセルの render 関数 (AdminUploadHistory/index.tsx と同判断)。
 */
import { useEffect, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { Link as RouterLink } from 'react-router'

import {
  Flex,
  Link,
  ListPager,
  TertiaryButton,
  Text,
  VStack,
} from '@enechain/ecloud-designsystem'

import { Badge } from '~/components/common/Badge'
import { CompactAlert } from '~/components/common/CompactAlert'
import {
  MasterDataTable,
  type MasterDataTableColumn,
} from '~/components/common/MasterDataTable'
import { PageBody } from '~/components/common/PageBody'
import { PageIntro } from '~/components/common/PageIntro'
import { PageLayout } from '~/components/common/PageLayout'
import { PageScrollArea } from '~/components/common/PageScrollArea'
import { RegionLoading } from '~/components/common/RegionLoading'
import { useAutoExecutionHistory } from '~/hooks/useAutoExecutionHistory'
import { useAutomationSettingCounts } from '~/hooks/useAutomationSettingCounts'
import {
  AUTO_EXECUTION_HISTORY_NO_VALUE,
  type AutoExecutionHistoryRowView,
  toAutoExecutionHistoryRowView,
} from '~/models/autoExecutionHistory'
import { AutomationSettingEntries } from '~/pages/AutoExecutionHistory/components/AutomationSettingEntries'
import { routePaths } from '~/routes/routePaths'

/** 1 ページの件数 (React mock と同じ)。 */
const PAGE_SIZE = 10

/**
 * 自動実行履歴 (`/workflow/auto-execution-history`)。
 * docs/specs/workflow/auto-execution-history.md AC-AEH-005〜011。 閲覧のみ (AC-AEH-007)。
 * flag の開閉は route 側 (`ReleaseRouteGate`) が持ち、 この画面は知らない。
 */
export const AutoExecutionHistoryPage = (): React.ReactElement => {
  const { t } = useTranslation()
  const [page, setPage] = useState(1)
  const state = useAutoExecutionHistory({ page, pageSize: PAGE_SIZE })
  const settingCounts = useAutomationSettingCounts()

  // 2 ページ目を見ている間に件数が減って page が範囲外になったら、 最終ページへ戻す
  // (空状態は出さない)。
  const lastPage = state.status === 'outOfRange' ? state.lastPage : undefined
  useEffect(() => {
    if (lastPage !== undefined) setPage(lastPage)
  }, [lastPage])

  const columns: Array<MasterDataTableColumn<AutoExecutionHistoryRowView>> = [
    {
      key: 'executedAt',
      header: t('page:autoExecutionHistory.column.executedAt'),
      nowrap: true,
      cell: (row) => row.executedAt,
    },
    {
      key: 'workflow',
      header: t('page:autoExecutionHistory.column.workflow'),
      cell: (row) => row.workflow,
    },
    {
      key: 'area',
      header: t('page:autoExecutionHistory.column.area'),
      cell: (row) => row.area,
    },
    {
      key: 'targetDate',
      header: t('page:autoExecutionHistory.column.targetDate'),
      nowrap: true,
      cell: (row) => row.targetDate,
    },
    {
      key: 'subStep',
      header: t('page:autoExecutionHistory.column.subStep'),
      cell: (row) => row.subStep,
    },
    {
      key: 'method',
      header: t('page:autoExecutionHistory.column.method'),
      cell: (row) => row.method,
    },
    {
      key: 'result',
      header: t('page:autoExecutionHistory.column.result'),
      nowrap: true,
      cell: (row) => <Badge status={row.result.tone}>{row.result.label}</Badge>,
    },
    {
      key: 'notes',
      header: t('page:autoExecutionHistory.column.notes'),
      cell: (row) =>
        row.notes.length === 0 ? (
          AUTO_EXECUTION_HISTORY_NO_VALUE
        ) : (
          <VStack alignItems="flex-start" gap={0}>
            {row.notes.map((note) => (
              <Text key={note}>{note}</Text>
            ))}
          </VStack>
        ),
    },
  ]

  const totalCount = state.status === 'ready' ? state.totalCount : 0
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE))
  const rows =
    state.status === 'ready'
      ? state.entries.map((entry) => toAutoExecutionHistoryRowView(entry, t))
      : []
  // 取得中は前の page の行が残るので、 件数は表示中の行の page から出す
  const from =
    ((state.status === 'ready' ? state.shownPage : page) - 1) * PAGE_SIZE + 1

  return (
    <PageLayout
      breadcrumbItems={[
        { label: t('common:nav.workflow') },
        { label: t('common:route.workflowAutoExecutionHistory'), bold: true },
      ]}
    >
      <PageBody>
        <PageIntro
          description={
            <>
              {t('page:autoExecutionHistory.description.beforeTemplates')}
              <Link asChild>
                <RouterLink to={routePaths.adminWorkflowTemplates.path}>
                  {t('page:autoExecutionHistory.description.templatesLink')}
                </RouterLink>
              </Link>
              {t('page:autoExecutionHistory.description.betweenLinks')}
              <Link asChild>
                <RouterLink to={routePaths.workflowCases.path}>
                  {t('page:autoExecutionHistory.description.casesLink')}
                </RouterLink>
              </Link>
              {t('page:autoExecutionHistory.description.afterCases')}
            </>
          }
        />
        <AutomationSettingEntries counts={settingCounts} />
        <Text as="h2" textStyle="pageTitle" margin={0}>
          {t('page:autoExecutionHistory.title')}
        </Text>

        {state.status === 'error' && (
          <CompactAlert
            actions={
              <TertiaryButton onClick={state.retry} size="sm">
                {t('page:autoExecutionHistory.error.retry')}
              </TertiaryButton>
            }
            role="alert"
            status="error"
            title={t('page:autoExecutionHistory.error.fetchFailed')}
          />
        )}

        {state.status === 'empty' && (
          <CompactAlert
            status="info"
            title={t('page:autoExecutionHistory.empty')}
          />
        )}

        {(state.status === 'loading' ||
          state.status === 'outOfRange' ||
          state.status === 'ready') && (
          <RegionLoading
            isLoading={state.status !== 'ready' || state.isFetching}
          >
            <PageScrollArea>
              <MasterDataTable
                columns={columns}
                getRowKey={(row) => row.key}
                isLoading={state.status !== 'ready'}
                rows={rows}
              />
            </PageScrollArea>
            {state.status === 'ready' && totalPages > 1 && (
              <Flex
                alignItems="center"
                justifyContent="space-between"
                flexWrap="wrap"
                gap={2}
                paddingTop={1}
              >
                <Text textStyle="body" color="Text.midEmphasis">
                  {t('page:autoExecutionHistory.pager.range', {
                    total: totalCount,
                    from,
                    to: from + rows.length - 1,
                  })}
                </Text>
                <ListPager current={page} goto={setPage} pages={totalPages} />
              </Flex>
            )}
          </RegionLoading>
        )}
      </PageBody>
    </PageLayout>
  )
}
